export type TransactionPinContext = 'rentalControl' | 'purchaseControl';
export type PinInputContext = 'parentalSettings' | 'parentalControls' | TransactionPinContext;

export const isTransactionContext = (context: PinInputContext): context is TransactionPinContext =>
  context === 'rentalControl' || context === 'purchaseControl';

export type ParentalSetting =
  | { isParentalControlActive: false; ageRestriction: undefined }
  | { isParentalControlActive: true; ageRestriction: number };

/** The slice of usePinStore the gate needs. Kept structural so the gate stays pure and testable. */
export interface PinGateState {
  validatedTitles: string[];
  isPurchasePinActive?: boolean;
  parentalSetting?: ParentalSetting;
}

export interface PinGateArgs {
  assetId?: string;
  ageRestriction?: number;
  context: PinInputContext;
}

export const assetIsAgeRestricted = (parentalSetting: ParentalSetting | undefined, assetAgeRestriction?: number) => {
  if (!parentalSetting?.isParentalControlActive || assetAgeRestriction === undefined) {
    return false;
  }
  return assetAgeRestriction > parentalSetting.ageRestriction;
};

// Decide in the event handler whether a pin prompt is needed, so callers either open the modal or run their action directly
export const isPinRequired = (state: PinGateState, { assetId, ageRestriction, context }: PinGateArgs): boolean => {
  // changing parental settings always requires the pin
  if (context === 'parentalSettings') {
    return true;
  }
  // pin already entered for this title earlier in the session
  if (assetId && state.validatedTitles.includes(assetId)) {
    return false;
  }
  if (assetIsAgeRestricted(state.parentalSetting, ageRestriction)) {
    return true;
  }
  // renting/purchasing bills the customer with no auth beyond the pin, so unknown settings fail closed
  return isTransactionContext(context) && state.isPurchasePinActive !== false;
};
